import { useCallback, useEffect, useRef, useState } from "react";
import { Navigate } from "react-router-dom";
import { ArrowPathIcon } from "@heroicons/react/24/outline";
import { debounce } from "lodash";

import PaginationInMemory from "../shared-components/PaginationInMemory";

import { usePermissionValidator } from "../api/UserPermissionValidators";
import {
  FilterSortHeader,
  FilterValues,
  GenericObject,
} from "./FilterSortHeader";
import { ThreeSixtyFinishedRequest } from "../api/ThreeSixty";
import {
  useGetThreeSixtyBlueprints,
  useGetThreeSixtyCompanies,
  useGetThreeSixtyFinishedList,
  useGetThreeSixtyFinishedNameFilterOptions,
} from "../api/ThreeSixtyAPI";
import { ThreeSixtyFinishedRowRenderer } from "./ThreeSixtyFinishedRowRenderer";
import SkeletonGrid from "../shared-components/SkeletonGrid";

type ThreeSixtyFinishedProps = {};
const ThreeSixtyFinished = (props: ThreeSixtyFinishedProps) => {
  const [cancel, setCancel] = useState<string[]>([]);
  const [preparingData, setPreparingData] = useState<boolean>(true);
  function toggleCancel(value: string): number {
    if (cancel.includes(value)) {
      const removed: string[] = cancel.filter((x) => x !== value);
      setCancel(removed);
      return -1;
    }
    cancel.push(value);
    setCancel(cancel);
    return 1;
  }

  const perms = usePermissionValidator();

  const [page, setPage] = useState<number>(1);
  const [sortField, setSortField] = useState<string>("finished_at");
  const [reverse, setReverse] = useState<boolean>(true);
  const [activeFilters, setActiveFilters] = useState<FilterValues[]>([]);
  const activeFiltersRef = useRef(activeFilters);
  activeFiltersRef.current = activeFilters;

  const getData = useRef(debounce(getDataImpl, 1200)).current;

  function setFilters(field: string, values: any[]) {
    const newFilters: FilterValues[] = activeFilters.filter(
      (a) => a.field !== field
    );
    if (values.length > 0) {
      newFilters.push({
        field: field,
        values: values,
      });
    }
    setActiveFilters(newFilters);
    setPage(1);
  }

  function setSortBy(field: string) {
    setPage(1);
    if (field === sortField) {
      if (reverse) {
        setSortField("");
      }
      setReverse(!reverse);
    } else {
      setSortField(field);
      setReverse(false);
    }
  }

  const {
    data: primaryData,
    mutate: primaryDataGet,
    isLoading: primaryDataLoading,
  } = useGetThreeSixtyFinishedList();
  const { mutateAsync: getNameFilterOptions } =
    useGetThreeSixtyFinishedNameFilterOptions();

  const loadNameFilterOptions = useCallback(
    async (search: string): Promise<GenericObject[]> => {
      const currentFilters = activeFiltersRef.current;
      const refs = currentFilters.find(
        (filter) => filter.field === "custom_id"
      );
      const dates = currentFilters.find(
        (filter) => filter.field === "finished_at"
      );
      const companies = currentFilters.find(
        (filter) => filter.field === "company_uuid"
      );
      const blueprints = currentFilters.find(
        (filter) => filter.field === "blueprint_uuid"
      );
      const names = await getNameFilterOptions({
        search,
        date: dates?.values[0],
        ref: refs?.values.join(","),
        company_uuids: companies?.values ?? [],
        blueprint_uuids: blueprints?.values ?? [],
      });
      return names.map((name) => ({ name }));
    },
    [getNameFilterOptions]
  );

  const { data: companyData, mutate: companyDataGet } =
    useGetThreeSixtyCompanies();

  useEffect(() => {
    companyDataGet();
  }, [companyDataGet]);

  const [companiesForFilters, setCompaniesForFilters] = useState<
    GenericObject[]
  >([]);
  useEffect(() => {
    const newCompanies = [];
    for (const co of companyData || []) {
      newCompanies.push({
        company_uuid: co.uuid,
        company_name: co.name,
      });
    }
    setCompaniesForFilters(newCompanies);
  }, [companyData, setCompaniesForFilters]);

  const { data: blueprintData } = useGetThreeSixtyBlueprints();

  const [blueprintsForFilters, setBlueprintsForFilters] = useState<
    GenericObject[]
  >([]);
  useEffect(() => {
    const newBlueprints = [];
    for (const bp of blueprintData || []) {
      newBlueprints.push({
        blueprint_uuid: bp.uuid,
        blueprint_name: bp.name,
      });
    }
    newBlueprints.sort((a, b) =>
      a.blueprint_name.localeCompare(b.blueprint_name)
    );
    setBlueprintsForFilters(newBlueprints);
  }, [blueprintData, setBlueprintsForFilters]);

  useEffect(() => {
    getData(activeFilters, sortField, reverse, page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeFilters, sortField, reverse, page]);

  function getDataImpl(
    activeFilters: FilterValues[],
    sortField: string,
    reverse: boolean,
    page: number
  ) {
    const names = activeFilters.filter((a) => a.field === "name");
    const refs = activeFilters.filter((a) => a.field === "custom_id");
    const dates = activeFilters.filter((a) => a.field === "finished_at");
    const companies = activeFilters.filter((a) => a.field === "company_uuid");
    const blueprints = activeFilters.filter(
      (a) => a.field === "blueprint_uuid"
    );
    const args: ThreeSixtyFinishedRequest = {
      company_uuids:
        companies && companies.length > 0 ? companies[0].values : [],
      blueprint_uuids:
        blueprints && blueprints.length > 0 ? blueprints[0].values : [],
      sortField: sortField,
      reverse: reverse,
      page: page,
    };
    if (names.length > 0) {
      args.names = names[0].values;
    }
    if (refs && refs.length > 0 && refs[0].values.length > 0) {
      args.ref = refs[0].values.join(",");
    }
    if (dates && dates.length > 0 && dates[0].values.length > 0) {
      args.date = dates[0].values[0];
    }
    setPreparingData(false);
    primaryDataGet(args);
  }

  if (!perms.canView360) {
    return <Navigate to="/unauthorized" replace />;
  }

  const loadingOrPreparingData = primaryDataLoading || preparingData;

  return (
    <div>
      <div className="absolute right-4 -top-8">
        <ArrowPathIcon
          className="w-5 stroke-green-500"
          onClick={() => getDataImpl(activeFilters, sortField, reverse, page)}
        />
      </div>
      <div className="flex gap-2 text-gray-500 text-xs font-medium bg-gray-50 p-2 border rounded-t-xl border-gray-300 uppercase">
        <FilterSortHeader
          headers={[
            {
              display: "Name",
              value_field: "name",
              filterable: true,
              sortable: true,
              searchable: true,
              loadFilterOptions: loadNameFilterOptions,
              filterOptionsDebounceMs: 1000,
            },
            {
              display: "Report ID",
              value_field: "request_uuid",
              filterable: false,
              sortable: false,
            },
          ]}
          sortField={sortField}
          reverse={reverse}
          sortCallback={setSortBy}
          data={primaryData ? primaryData[1] : []}
          activeFilters={activeFilters}
          setFilterCallback={setFilters}
        />
        <FilterSortHeader
          headers={[
            {
              display: "Insights",
              value_field: "",
              filterable: false,
              sortable: false,
            },
          ]}
          sortField={sortField}
          sortCallback={setSortBy}
          reverse={reverse}
          data={[]}
          activeFilters={activeFilters}
          setFilterCallback={setFilters}
        />
        <FilterSortHeader
          headers={[
            {
              display: "Company",
              key_field: "company_uuid",
              value_field: "company_name",
              filterable: true,
              sortable: true,
            },
          ]}
          sortField={sortField}
          sortCallback={setSortBy}
          reverse={reverse}
          data={companiesForFilters}
          activeFilters={activeFilters}
          setFilterCallback={setFilters}
        />
        <FilterSortHeader
          headers={[
            {
              display: "Check Type",
              key_field: "blueprint_uuid",
              value_field: "blueprint_name",
              filterable: true,
              sortable: true,
            },
            {
              display: "Behavior Kit",
              value_field: "behavior_kit_name",
              filterable: false,
              sortable: false,
            },
          ]}
          sortField={sortField}
          sortCallback={setSortBy}
          reverse={reverse}
          data={blueprintsForFilters}
          activeFilters={activeFilters}
          setFilterCallback={setFilters}
        />
        <FilterSortHeader
          headers={[
            {
              display: "Met SLA?",
              value_field: "",
              filterable: false,
              sortable: false,
            },
          ]}
          sortField={sortField}
          sortCallback={setSortBy}
          reverse={reverse}
          data={[]}
          activeFilters={activeFilters}
          setFilterCallback={setFilters}
        />
        <FilterSortHeader
          headers={[
            {
              display: "Date Completed",
              value_field: "finished_at",
              filterable: true,
              sortable: true,
              datePick: true,
            },
          ]}
          sortField={sortField}
          sortCallback={setSortBy}
          reverse={reverse}
          data={[]}
          activeFilters={activeFilters}
          setFilterCallback={setFilters}
        />
        <FilterSortHeader
          headers={[
            {
              display: "Client Ref #",
              value_field: "custom_id",
              filterable: true,
              sortable: true,
              searchable: true,
            },
          ]}
          sortField={sortField}
          sortCallback={setSortBy}
          reverse={reverse}
          data={primaryData ? primaryData[1] : []}
          activeFilters={activeFilters}
          setFilterCallback={setFilters}
        />
        <FilterSortHeader
          headers={[
            {
              display: "Actions",
              value_field: "",
              filterable: false,
              sortable: false,
            },
          ]}
          sortField={sortField}
          sortCallback={setSortBy}
          reverse={reverse}
          data={[]}
          activeFilters={activeFilters}
          setFilterCallback={setFilters}
        />
      </div>
      <div className="bg-white border rounded-b-xl border-gray-300">
        {loadingOrPreparingData ? (
          <div className="p-2">
            <SkeletonGrid numberOfRows={8} />
          </div>
        ) : (!primaryData || primaryData[1].length === 0) &&
          !loadingOrPreparingData ? (
          <div className="p-2">no data found</div>
        ) : (
          <div className="text-xs">
            {primaryData?.[1].map((row) => (
              <ThreeSixtyFinishedRowRenderer
                key={row.request_uuid}
                row={row}
                cancel={cancel}
                toggleCancel={toggleCancel}
              />
            ))}
          </div>
        )}
      </div>
      {!loadingOrPreparingData &&
        ((primaryData && primaryData[0]) || 0) > 0 && (
          <div className="pb-4">
            <PaginationInMemory
              totalCount={(primaryData && primaryData[0]) || 0}
              currentPage={page}
              pageSize={20}
              showDescription={true}
              pageChange={setPage}
              activeStyle="bg-green-50"
            />
          </div>
        )}
    </div>
  );
};

export default ThreeSixtyFinished;
